/**
 * @file ImageFrameStep.tsx
 * @description Шаг «Кадр под блок» у элемента «Фото» (phase19.16, партия
 * SB120, этап 1; uiux-sb120-stage1 §3 — «при загрузке открывается шаг §2.4,
 * рамка — по доле колонки и её высоте»; требование пользователя «при
 * загрузке фото адаптировать под колонку»). Общий шаг — `FrameStep`; здесь:
 * рамка = доля колонки × ширина блока на ПК (телефон — 360) × высота по
 * режиму («По колонке» — «не меньше», «Числом» — число, «По фото» —
 * пропорции кадра); пишет `focus/focusPhone/aspect/aspectPhone`. Масштаба
 * у элемента в схеме нет — поле масштаба скрыто; при «По фото» вместо него —
 * пропорции кадра.
 */

import { DEFAULT_SITE_THEME, type SectionElementOf } from "@chaiberi/shared";

import { useSectionForm } from "../section-form-context";
import { FrameStep } from "../look/FrameStep";
import { mediaAspect } from "../../../../hooks/site-builder/useMediaSizes";
import { frameToImage, imageFrameAspect, imageToFrame } from "../../../../lib/site-builder/frame-draft";
import { lookWarningText } from "../../../../lib/site-builder/look-warnings";

type Image = SectionElementOf<"image">;

interface Props {
  open: boolean;
  element: Image;
  /** Ширина колонки на ПК, px. */
  columnWidth: number;
  onChange: (element: Image) => void;
  onClose: () => void;
}

export function ImageFrameStep({ open, element, columnWidth, onChange, onClose }: Props) {
  const { mediaSizes, theme } = useSectionForm();
  const factor = (theme ?? DEFAULT_SITE_THEME).spacing.phoneFactor;
  if (!element.mediaId) return null;
  const mediaId = element.mediaId;
  const fileAspect = mediaAspect(mediaSizes, mediaId);
  const fileWidth = mediaSizes[mediaId]?.width ?? null;

  return (
    <FrameStep
      open={open}
      mediaId={mediaId}
      initial={imageToFrame(element)}
      numbers={element.height.kind === "photo" ? "aspect" : "none"}
      aspectOf={(device, draft) => imageFrameAspect(element, draft, device, columnWidth, fileAspect, factor)}
      warningOf={(device) => {
        const frame = Math.round(device === "phone" ? 360 : columnWidth);
        return fileWidth && fileWidth < frame
          ? lookWarningText({ code: "photo_smaller_than_frame", path: device === "phone" ? "columns.mediaIdPhone" : "columns.item", value: frame }, { fileWidth })
          : null;
      }}
      onDone={(draft) => {
        onClose();
        onChange(frameToImage(element, draft));
      }}
      onClose={onClose}
    />
  );
}
